/**
 * The buff bar: the hero's ACTIVE timed effects as a strip of icons.
 *
 * Pure projection — `observation + tick` in, display entries out. The client
 * never re-derives a magnitude or an expiry: both are the sim's numbers and
 * are formatted here, once. The bar's data is the same one the sim keeps for
 * itself (`player.buffs`, one entry per type, plus the two class windows that
 * live outside that list), so the bar can never disagree with the fight.
 */
import type { BuffType, Observation } from "@pd/shared";
import type { BuffIcon } from "./ability-icons.js";
import { isBound } from "./status-fx.js";

export interface BuffEntry {
  /** Stable identity — the bar rebuilds its tiles when this set changes, and
   *  only updates the countdown text in place otherwise (traps/dom-rebuild.md). */
  id: string;
  label: string;
  icon: BuffIcon;
  /** The multiplier as the player reads it: `×1.1`, or `+10%` for accuracy
   *  (whose 100 baseline is the table roll, not a multiplier). */
  magnitudeText: string;
  /** The countdown: `45s`, or `1:23` past a minute. */
  timeText: string;
  /** True when the effect HURTS (a damage-taken curse, a slowed walk): the
   *  tile reads red so a debuff never passes for a buff. */
  debuff: boolean;
  /** The hover line: what the effect actually does, in the same voice as the
   *  dock's ability hints. */
  hint: string;
}

/** The display order: the sim's buff types (BUFF_TYPES order), then the two
 *  class windows that are not in that list. Fixed, so an icon never jumps. */
const BUFF_ORDER: BuffType[] = ["damage", "damage_taken", "speed", "accuracy"];

const BUFF_LABEL: Record<BuffType, string> = {
  damage: "Damage",
  damage_taken: "Damage taken",
  speed: "Speed",
  accuracy: "Accuracy",
};

const BUFF_ICON: Record<BuffType, BuffIcon> = {
  damage: "buff-damage",
  damage_taken: "buff-damage-taken",
  speed: "buff-speed",
  accuracy: "buff-accuracy",
};

/** Hover text, one pair per family so a debuff never reads as a buff. Same
 *  register as the dock's ability hints (ability-tooltip.ts). */
const BUFF_HINT: Record<BuffType, { good: string; bad: string }> = {
  damage: {
    good: "Your weapon hits harder while this holds.",
    bad: "Your weapon hits softer while this holds.",
  },
  damage_taken: {
    good: "You take less damage while this holds.",
    bad: "You take more damage while this holds.",
  },
  speed: {
    good: "You cover ground faster while this holds.",
    bad: "You cover ground slower while this holds.",
  },
  accuracy: {
    good: "Your swings land more often while this holds.",
    bad: "Your swings land less often while this holds.",
  },
};

const BARRICADE_HINT = "Incoming blows pass harmlessly while this holds.";
const REFLECT_HINT = "Absorbs part of each hit and returns that damage to its attacker.";
const BOUND_HINT = "Movement stopped while this holds. Attacks, abilities and teleports remain available.";

/** 20 ticks = 1 s (TICK_MS = 50, shared). The sim's clock; the client only
 *  divides it for display, exactly like the floor timer and the exit countdown. */
const TICKS_PER_SECOND = 20;

export function buildBuffs(obs: Observation, meId: number, tick: number): BuffEntry[] {
  // No `!` here: on my own leave tick I am no longer in the observation
  // (docs/traps/leaver-last-tick.md) and there is nothing to show.
  const me = obs.players.find((p) => p.id === meId);
  if (!me) return [];

  const out: BuffEntry[] = [];
  for (const type of BUFF_ORDER) {
    const buff = me.buffs?.find((b) => b.type === type);
    // The sim clears an expired buff inside its own step pass, so a present
    // entry is an active one; the guard is for hand-built observations.
    if (!buff || buff.until <= tick) continue;
    const debuff = isDebuff(type, buff.magnitude);
    out.push({
      id: `buff:${type}`,
      label: BUFF_LABEL[type],
      icon: BUFF_ICON[type],
      magnitudeText: magnitudeText(type, buff.magnitude),
      timeText: timeText(buff.until - tick),
      debuff,
      hint: BUFF_HINT[type][debuff ? "bad" : "good"],
    });
  }

  // Barricade and Reflect are timed windows the sim keeps OUTSIDE the buff
  // list (they are per-ability state, not (type, magnitude, expiry) rows).
  // They belong on the bar: before it existed, a gold border on the dock
  // button was the only sign they were up.
  if (me.damageImmuneUntil > tick) {
    out.push({
      id: "window:barricade",
      label: "Barricade",
      icon: "buff-immune",
      magnitudeText: "×0",
      timeText: timeText(me.damageImmuneUntil - tick),
      debuff: false,
      hint: BARRICADE_HINT,
    });
  }
  if (me.reflectUntil > tick) {
    out.push({
      id: "window:reflect",
      label: "Reflect",
      icon: "buff-reflect",
      magnitudeText: `${me.reflectPct}%`,
      timeText: timeText(me.reflectUntil - tick),
      debuff: false,
      hint: REFLECT_HINT,
    });
  }
  // Bound is a status the sim owns per-hero (not a multiplier row), so it
  // rides the bar like the two class windows. It has no percentage: the tile
  // carries the countdown, and the hover card says what it does.
  if (isBound(me, tick)) {
    out.push({
      id: "status:freeze",
      label: "Bound",
      icon: "buff-freeze",
      magnitudeText: "\u2014",
      timeText: timeText(me.frozenUntil! - tick),
      debuff: true,
      hint: BOUND_HINT,
    });
  }
  return out;
}

function magnitudeText(type: BuffType, magnitude: number): string {
  if (type === "accuracy") return `${magnitude >= 100 ? "+" : "-"}${Math.abs(magnitude - 100)}%`;
  // Up to two decimals, trailing zeros trimmed: 110 → ×1.1, 75 → ×0.75.
  // Rounding to one decimal would print a ×0.75 damage-taken buff as ×0.8,
  // which is a different number than the one the sim is applying.
  return `×${(magnitude / 100).toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}`;
}

function timeText(remainingTicks: number): string {
  const secs = Math.max(0, Math.ceil(remainingTicks / TICKS_PER_SECOND));
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
}

/** Below 100 means less of a good thing; for damage_taken, above 100 means
 *  more of a bad one. */
function isDebuff(type: BuffType, magnitude: number): boolean {
  return type === "damage_taken" ? magnitude > 100 : magnitude < 100;
}
